/**
 * The home screen: this kennel's litters, grouped by where each one is in its
 * life.
 *
 * A litter and not a flat list of puppies, which is what used to be here. The
 * flat list answered "every newborn I am raising", which is the right question
 * for a kennel with one litter in the house and the wrong one for a kennel with
 * two — five white puppies from one and four from another, in one column, is a
 * list nobody can act on. A litter is the unit a breeder thinks in, plans in and
 * sells out of, so it is the unit the app is organised by; the puppies are one
 * tap in, on the dashboard where they can be compared against each other.
 *
 * The order of the groups is the order of attention: what is happening now, what
 * is coming, what is done. Historical litters are last and will one day be the
 * longest section — which is fine, because nobody scrolls to them by accident.
 *
 * There is no "you belong to no tenant" branch here: without a tenant there is
 * no app at all, so that gate is in __root.tsx and by the time this renders
 * there is always a tenant.
 *
 * For most kennels most of the time this screen is a formality — one litter is
 * in the house and everything happens inside it — so on the first navigation of
 * an app load it steps out of the way and hands over to that litter. See
 * `landing` below for the shape of "first" and for what holds the jump back.
 */
import {
  canSeePlannedLitters,
  canWriteLitters,
  type Litter,
  type LitterStatus,
} from '@puppy-doctor/platform';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useInvitesForEmail } from '@/api/invites.ts';
import { useLitters } from '@/api/litters.ts';
import { inLitter, useOffspring } from '@/api/offspring.ts';
import { soleActive } from '@/lib/litters.ts';
import { LitterCard } from '@/components/LitterCard.tsx';
import { PendingInvites } from '@/components/PendingInvites.tsx';
import { buttonClasses } from '@/components/ui.tsx';
import { useProfile, useTenant, useTenantId } from '@/tenant/TenantProvider.tsx';

export const Route = createFileRoute('/')({
  loader: ({ context }) => {
    // The tenant is app state rather than a route param (see TenantProvider),
    // so the loader cannot know it. It warms nothing here; the component's own
    // hooks are the fetch. Left in place as the seam for when a tenant-scoped
    // route needs it.
    void context.queryClient;
  },
  component: Home,
});

const GROUPS: readonly LitterStatus[] = ['active', 'incoming', 'historical'];

/**
 * The same list without the planned litters, for a viewer — see
 * `canSeePlannedLitters`.
 *
 * The groups are narrowed rather than the litters filtered, and that is the
 * whole of the hiding: a status with no group drawn for it is a status whose
 * litters never reach the screen, and the heading that would announce one goes
 * with it. `oneActive` below reads `active` only, so the jump past this screen
 * is unaffected either way.
 */
const SETTLED_GROUPS: readonly LitterStatus[] = ['active', 'historical'];

/**
 * Whether this app load still owes the reader a jump past this screen.
 *
 * A kennel with one litter in the house has one screen it ever wants, and the
 * list in front of it is a tap that carries no information: one card, tapped,
 * every morning. So the FIRST navigation of an app load — a cold start, a PWA
 * relaunch, a reload — goes straight to that litter, and every visit to `/`
 * after it draws the list, because by then the only ways back here are the
 * header mark and the breadcrumb, and both of them are somebody asking for the
 * list. Yanking them out of it would leave the app with no list at all.
 *
 * Module scope is exactly the lifetime wanted: one app load, reset by a reload,
 * and nowhere a stale value could be persisted and read back.
 *
 * The entry path is part of it because a load that started on a puppy's page is
 * not a load that has skipped anything — the reader who then taps the mark is
 * asking for the list, the same as any other later visit.
 */
let landing = window.location.pathname === '/';

function Home() {
  const { t } = useTranslation();
  const tenantId = useTenantId();
  const { role } = useTenant();
  const { data: litters, isPending } = useLitters(tenantId);
  const { data: offspring } = useOffspring(tenantId);

  /**
   * An invitation to a SECOND kennel, which is the only place it can be seen.
   *
   * The onboarding screen shows waiting invitations, but it only renders for
   * somebody who belongs to no kennel at all — so a vet already in one had no way
   * to discover they had been invited to another. It costs one listener on a
   * query that is almost always empty. See components/PendingInvites.tsx.
   */
  const { email } = useProfile();
  const { data: invites } = useInvitesForEmail(email);

  const mayAdd = role !== null && canWriteLitters(role);

  /**
   * A viewer is shown the litters that exist and not the ones that are planned —
   * see `canSeePlannedLitters`. A mating that may not take is the kennel's own
   * business, and the group heading alone would announce it.
   *
   * Filtered here rather than in the query, and so not in the security rules
   * either: the reason is written out in app/firestore.rules above the `litters`
   * match, and it is that a planned litter's puppies and weighings hang off the
   * tenant with no status on them, so gating the one document would hide a name
   * and leave its contents. The litter's own page turns the same question away
   * for anybody who follows a link past this screen.
   */
  /**
   * A viewer is shown the litters that exist and not the ones that are planned.
   * A mating that may not take is the kennel's own business, and a role that is
   * not known yet — the fraction of a second before the membership listener
   * answers, and anybody who is not a member at all — counts as not allowed: of
   * the two ways to be wrong in that window, only one of them is a leak.
   *
   * Narrowed here rather than in the query, and so not in the security rules
   * either: the reason is argued above the `litters` match in
   * app/firestore.rules, and it is that a planned litter's puppies and weighings
   * hang off the tenant with no status on them, so gating the one document would
   * hide a name and leave its contents. The litter's own page turns the same
   * question away for anybody who follows a link past this screen.
   */
  const groups = role !== null && canSeePlannedLitters(role) ? GROUPS : SETTLED_GROUPS;
  const all = litters ?? [];
  const visible = all.filter((litter: Litter) => groups.includes(litter.status));

  /**
   * The jump past this screen, taken at most once per app load — see `landing`.
   *
   * It waits for BOTH listeners, not just the litters one. Skipping the home
   * screen while the invitations query is still in flight would hide the one
   * place an invitation to a second kennel is ever shown, and an invitation is a
   * thing to act on: while one is waiting, this screen has something on it that
   * the litter underneath does not, so the jump is given up rather than delayed.
   *
   * `replace`, because the entry it would otherwise push is a screen the reader
   * never saw — and back out of a home screen should leave the app, not walk
   * them through a list they were deliberately spared. The list stays one tap
   * away, on the mark in the header and on the breadcrumb over the litter.
   *
   * Which litter it jumps to is `soleActive` in lib/litters.ts — the same rule
   * the share-target screen uses, so a kennel taken straight to its litter every
   * morning is not asked to pick one when it shares photographs in.
   */
  const settled = !isPending && invites !== undefined;
  const jumpTo = landing && settled && invites.length === 0 ? soleActive(all) : null;

  const navigate = useNavigate();
  useEffect(() => {
    if (!landing || !settled) return;
    landing = false;
    if (jumpTo === null) return;

    void navigate({
      to: '/litters/$litterId',
      params: { litterId: jumpTo },
      replace: true,
    });
  }, [jumpTo, navigate, settled]);

  return (
    <div className="flex flex-col gap-5">
      {/* Above the litters, because an invitation is a thing to act on and a
          list of litters is a thing to read. It is drawn at all only when one is
          waiting — see the component. */}
      <PendingInvites invites={invites ?? []} />

      <div className="flex items-center gap-3">
        <h1 className="flex-1 text-2xl font-bold tracking-[-0.02em]">{t('litter.title')}</h1>
        {mayAdd && (
          <Link
            to="/litters/new"
            className={buttonClasses({ className: 'min-h-tap gap-1.5 px-4 text-sm' })}
          >
            <Plus className="size-4" aria-hidden />
            {t('litter.add')}
          </Link>
        )}
      </div>

      {isPending || jumpTo !== null ? (
        // `jumpTo` is a frame the reader is not meant to see: the effect below
        // navigates on this same commit, and painting the card first would make
        // the jump look like a mis-tap.
        <p className="text-ink-faint">{t('common.loading')}</p>
      ) : visible.length === 0 ? (
        <Empty mayAdd={mayAdd} />
      ) : (
        groups.map((status) => {
          const group = all.filter((litter: Litter) => litter.status === status);
          if (group.length === 0) return null;

          return (
            <section key={status} className="flex flex-col gap-2">
              {/* The heading is quiet on purpose: the group it names is already
                  obvious from the badges inside it, and three loud headings on a
                  screen with two litters on it would be more chrome than
                  content. It is here for the reader who is scrolling to the
                  section, and for the screen reader that needs the landmark. */}
              <h2 className="text-xs font-semibold tracking-wide text-ink-faint uppercase">
                {t(`litter.status.${status}`)}
              </h2>
              {group.map((litter) => (
                <LitterCard
                  key={litter.id}
                  litter={litter}
                  offspring={inLitter(offspring, litter.id)}
                />
              ))}
            </section>
          );
        })
      )}
    </div>
  );
}

const Empty = ({ mayAdd }: { mayAdd: boolean }) => {
  const { t } = useTranslation();

  return (
    <div className="py-12 text-center">
      <p className="font-medium text-ink-soft">{t('litter.empty')}</p>
      <p className="mx-auto mt-1 max-w-[34ch] text-sm text-ink-faint">
        {mayAdd ? t('litter.emptyHint') : t('litter.emptyReadOnly')}
      </p>
      {mayAdd && (
        <Link to="/litters/new" className={buttonClasses({ className: 'mt-5 min-h-tap' })}>
          {t('litter.addFirst')}
        </Link>
      )}
    </div>
  );
};
